import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import { MainLeftTitle } from "../../../_uiComponents/WrapperComponents";
import LcvManualPrizeWinsGrid from "./views/LcvManualPrizeWinsGrid";
import LcvManualPrizeWinsGridSkeleton from "./views/LcvManualPrizeWinsGridSkeleton";
import LcvFulfilmentDialog from "./views/LcvFulfilmentDialog";
import LcvCampaignSelect from "../shared/LcvCampaignSelect";
import useLcvCampaign from "../shared/useLcvCampaign";

import { lcvManualPrizeWinsConstants } from "../../../../_redux/_constants/lcvManualPrizeWins.constants";
import {
     lcvManualPrizeWinsGetRequest,
     lcvManualPrizeWinsSetFulfilmentRequest,
     lcvManualPrizeWinsClearActionResult,
} from "../../../../_redux/_actions/lcvManualPrizeWins.action";

// The physical prizes that have been won, and whether each one has been posted.
//
// Searching and paging are backend parameters, so the rows on screen are exactly the page the
// backend returned and nothing is narrowed here.

const INITIAL_PAGE = { skip: 0, take: 25 };

export default function LcvManualPrizeWinsWrapper() {
     const actionDispatch = useDispatch();

     const winsPage = useSelector((state) => state?.lcvManualPrizeWins?.wins);
     const actionResult = useSelector((state) => state?.lcvManualPrizeWins?.actionResult);

     const pendingRequests = useSelector((state) => state?.loading?.pendingRequests || []);

     const isLoading = pendingRequests.includes(
          lcvManualPrizeWinsConstants.LCV_MANUAL_PRIZE_WINS_GET_REQUEST,
     );
     const isBusy = pendingRequests.includes(
          lcvManualPrizeWinsConstants.LCV_MANUAL_PRIZE_WINS_SET_FULFILMENT_REQUEST,
     );

     const { selectedCampaignId } = useLcvCampaign();

     const [page, setPage] = useState(INITIAL_PAGE);
     const [searchTerm, setSearchTerm] = useState("");
     const [winBeingFulfilled, setWinBeingFulfilled] = useState(null);

     const listQuery = useMemo(
          () => ({
               page: Math.floor(page.skip / page.take) + 1,
               pageSize: page.take,
               searchTerm: searchTerm.trim() || undefined,
          }),
          [page, searchTerm],
     );

     useEffect(() => {
          if (!selectedCampaignId) {
               return;
          }

          actionDispatch(lcvManualPrizeWinsGetRequest(listQuery));
     }, [actionDispatch, listQuery, selectedCampaignId]);

     useEffect(() => {
          return () => {
               actionDispatch(lcvManualPrizeWinsClearActionResult());
          };
     }, [actionDispatch]);

     // The page being viewed goes with the write, so the reload the epic fires lands on the same
     // page rather than sending the reader back to the first one.
     const confirmFulfilment = useCallback(
          (payload) => {
               actionDispatch(lcvManualPrizeWinsSetFulfilmentRequest({ ...payload, listQuery }));
               setWinBeingFulfilled(null);
          },
          [actionDispatch, listQuery],
     );

     const rows = winsPage?.wins || [];
     const outcome = typeof actionResult === "string" ? actionResult : actionResult?.message;

     return (
          <>
               <MainLeftTitle title="LCV Physical Prizes" />

               <div className="sba-telerik-components-wr">
                    <LcvCampaignSelect />

                    {outcome && (
                         <Alert severity="success" sx={{ mb: 2 }}>
                              {outcome}
                         </Alert>
                    )}

                    <Box sx={{ mb: 2, maxWidth: 360 }}>
                         <TextField
                              label="Search"
                              value={searchTerm}
                              onChange={(event) => {
                                   setSearchTerm(event.target.value);
                                   setPage((current) => ({ ...current, skip: 0 }));
                              }}
                              helperText="Name, email, dealer or prize"
                              fullWidth
                              size="small"
                         />
                    </Box>

                    {isLoading && rows.length === 0 ? (
                         <LcvManualPrizeWinsGridSkeleton />
                    ) : (
                         <LcvManualPrizeWinsGrid
                              wins={rows}
                              totalCount={winsPage?.totalCount || 0}
                              page={page}
                              isBusy={isBusy}
                              onPageChange={setPage}
                              onMarkFulfilled={setWinBeingFulfilled}
                         />
                    )}

                    {winBeingFulfilled && (
                         <LcvFulfilmentDialog
                              win={winBeingFulfilled}
                              isBusy={isBusy}
                              onCancel={() => setWinBeingFulfilled(null)}
                              onConfirm={confirmFulfilment}
                         />
                    )}
               </div>
          </>
     );
}
